// imports

import _ from 'lodash'
import { ordinal } from 'humanize-plus'

import {
  evaluateFbAnswer,
  adjustPointsAvailableForContentHint,
  adjustPointsAvailableForAutoHint
} from '../lib/legacy/legacyEngine'

// constants

// The minimum number of milliseconds a student must ponder a question before giving up.
// If they give up before the minimum time, they are chided by this nagging app for not trying hard enough.
export const MINIMUM_QUESTION_TIME = 5000

export const defaultState = {
  activity: null,
  uiState: {
    showArgaError: false,
    isInstructorView: false,
    radioButtons: {
      originalScoreSelected: false,
      classScoreSelected: false
    }
  },
  config: null
}

// activity flow and control helper functions
// user just completed the question, but hasn't gone to the next question yet
export function didActivityJustCrossHalfway (oldActivity, newActivity) {
  let activityJustCrossedHalfway = false
  if (!oldActivity.score.curCard.completed && newActivity.score.curCard.completed) {
    activityJustCrossedHalfway = (
      (oldActivity.score.activityScore / oldActivity.score.scoreTarget) < 0.5 && // previous score less than half
      (newActivity.score.activityScore / newActivity.score.scoreTarget) >= 0.5 && // new score more than half
      newActivity.score.activityScore < newActivity.score.scoreTarget // not complete yet
    )
  }
  return activityJustCrossedHalfway
}

export function didActivityJustComplete (oldActivity, newActivity) {
  const activityJustCompleted = oldActivity.score.activityScore < oldActivity.score.scoreTarget &&
    newActivity.score.activityScore >= newActivity.score.scoreTarget
  return activityJustCompleted
}
// end activity helpers

// modal helper functions

export function shouldShowIncorrectModal (activity) {
  const curCard = activity.score.curCard
  if (curCard.questionType === 'FB') {
    let correctAnswer
    if (curCard.userAnswer) {
      correctAnswer = _.findIndex(curCard.answers, (answer) => {
        // check if inputs are integers or decimals
        if ((!isNaN(parseFloat(answer)) && !isNaN(parseFloat(curCard.userAnswer)))) {
          return evaluateFbAnswer(curCard)
        }
        return curCard.userAnswer.toLowerCase().replace(/\s/g, '') === answer.toLowerCase().replace(/\s/g, '')
      })
    }
    return curCard.userAnswer !== null && correctAnswer === -1
  } else {
    return curCard.userAnswerIndex !== null && curCard.userAnswerIndex !== curCard.correctAnswerIndex
  }
}

export function shouldShowSuccessModal (activity) {
  return activity.score.curCard.completed && !activity.score.curCard.givenUp
}

export function isSplitScreenEbookAvailable (activity) {
  // if it's a URL, it's a VitalSource e-book on an external host
  const a = document.createElement('a')
  const ebookString = activity.score.curCard.meta.ebook
  let potentialUrl = /\s*\[\[(.*)\]\]\s*/.exec(ebookString)

  if (potentialUrl != null) {
    potentialUrl = potentialUrl[1]
  }

  a.href = potentialUrl
  return (a.host && a.host !== window.location.host)
}

// end modal helpers

// hint helper functions
export function getHint (score) {
  if (canShowContentHint(score.curCard)) {
    adjustPointsAvailableForContentHint(score)
    return contentHint(score.curCard)
  } else if (canShowAutoHint(score.curCard)) {
    adjustPointsAvailableForAutoHint(score)
    return autoHint(score.curCard)
  } else {
    return noHintsLeft()
  }
}

function canShowContentHint (curCard) {
  if (!curCard.takenContentHint && curCard.meta.content_hint) {
    return true
  }
  return false
}

function canShowAutoHint (curCard) {
  if (!curCard.takenContentHint && curCard.meta.content_hint) {
    return false
  } else if (curCard.questionType === 'MC' || curCard.questionType === 'SC') {
    if (remainingWrongAnswers(curCard).length > 1) {
      return true
    }
    return false
  } else if (curCard.questionType === 'FB') {
    if (curCard.maxHelps > curCard.numHelps) {
      return true
    }
    return false
  } else {
    throw new Error('No hint for question type yet')
  }
}

function remainingWrongAnswers (curCard) {
  return curCard.choices.filter((choice, index) => {
    if (curCard.answered.includes(index) ||
      curCard.hinted.includes(index) ||
      index === curCard.correctAnswerIndex) {
      return false
    }
    return true
  })
}

function contentHint (curCard) {
  curCard.takenContentHint = true
  return {
    type: 'CONTENT_HINT',
    message: curCard.meta.content_hint
  }
}

function autoHint (curCard) {
  if (curCard.questionType === 'MC' || curCard.questionType === 'SC') {
    return autoHintForMCandSC(curCard)
  } else if (curCard.questionType === 'FB') {
    return autoHintForFB(curCard)
  } else {
    throw new Error('hint not supported')
  }
}

function autoHintForMCandSC (curCard) {
  const wrongAnswers = remainingWrongAnswers(curCard)
  const answer = wrongAnswers[Math.floor(Math.random() * wrongAnswers.length)]
  const answerIndex = curCard.choices.indexOf(answer)
  const feedback = curCard.choices_fb[answerIndex]
    ? curCard.choices_fb[answerIndex].replace(/(.*)\{\{(.*)\}\}/, '$1')
    : null
  curCard.hinted.push(answerIndex)
  curCard.numHelps++
  return {
    type: 'MCSC_HINT',
    answer,
    answerIndex,
    feedback
  }
}

function getEmptyIndexes (obj) {
  const indexes = []
  _.forEach(obj, (val, index) => {
    if (!val) {
      indexes.push(index)
    }
  })
  return indexes
}

function autoHintForFB (curCard) {
  const answerWords = curCard.testTerm.split(' ')
  let meta
  const lengths = _.map(answerWords, (word) => {
    return word.length
  })
  const letters = _.flatMap(answerWords, (word) => {
    return word.split('')
  })
  const placeholder = curCard.hinted.length ? _.cloneDeep(curCard.hinted[curCard.hinted.length - 1].letters) : {}
  if (_.isEmpty(placeholder)) {
    _.forEach(letters, (a, i) => {
      placeholder[i + 1] = ''
    })
    meta = 'There are ' + letters.length + ' characters in the answer'
  } else {
    const indexes = getEmptyIndexes(placeholder, '')
    const indexOf = Math.floor(Math.random() * indexes.length)
    const index = indexes[indexOf]
    if (placeholder[index] === '') {
      placeholder[index] = letters[index - 1]
      meta = 'The ' + ordinal(index) + ' character of the answer is ' + letters[index - 1]
    }
  }
  curCard.numHelps++
  curCard.hinted.push({
    letters: placeholder,
    wordsLength: lengths
  })
  return {
    type: 'FB_HINT',
    lengths,
    placeholder,
    meta
  }
}

function noHintsLeft () {
  return {
    type: 'NO_HINT'
  }
}
// end hint helpers
